/****************************************************
 * Module Name:  singleUserPermission.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState, useMemo } from "react";
import UserProxy from "../../utility/proxy/manager/userProxy";
import Select from "react-select";
import { Box, Typography, Tooltip } from "@mui/material";
import Constants from "../../utility/constants";
import DualListBox from "react-dual-listbox";
import UserPermissionProxy from "../../utility/proxy/manager/permissionProxy/userPermissionProxy";
import PermissionProxy from "../../utility/proxy/manager/permissionProxy/permissionProxy";
import "react-dual-listbox/lib/react-dual-listbox.css";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import BlockIcon from "@mui/icons-material/Block";
import AddTaskIcon from "@mui/icons-material/AddTask";
import Utility from "../../utility/utility";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import { LoadingButton } from "@mui/lab";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";

const fullOptionToValue = (userId, selectedData) => {
  let arr = [];
  selectedData.map((option) => {
    arr.push({
      userid: userId,
      permissionid: option.sendValue,
      type: option.type,
    });
    return arr;
  });
  return arr;
};

const SingleUserPermission = () => {
  const [user, setUser] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [permissionOption, setPermissionOption] = useState([]);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [grantLoader, setGrantLoader] = useState(false);
  const [revokeLoader, setRevokeLoader] = useState(false);
  const [userInputValue, setUserInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  // eslint-disable-next-line
  const [local_Permission, setLocal_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.PERMISSION_USERPERMISSION_USER
    )
  );

  useEffect(() => {
    getAllUser();
    getAllUserPermission();
  }, []);

  const onChange = (value) => {
    setSelected(value);
  };

  const getAllUser = () => {
    UserProxy.GetAllGenericUserMini((response) => {
      if (response) {
        setAllUserData(response)
      } else {
        setErrorSnackbar(true)
      }
    }
    );
  };

  const setAllUserData = (response) => {
    const sortedResponse = Utility.sortFn(response, "username");
    let optionsArray = [];
    sortedResponse.map((data) => {
      let option = { value: data.id, label: data.username };
      optionsArray.push(option);
      return true;
    });
    setUserOptions(optionsArray);
    setUser(user === "" && optionsArray.length > 0 ? optionsArray[0] : user);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInputValue(userInputValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [userInputValue]);

  const filteredUserOptions = useMemo(() => {
    if (!debouncedInputValue) {
      return userOptions.slice(0, 100);
    }
    return userOptions.filter(option =>
      option.label.toLowerCase().includes(debouncedInputValue.toLowerCase())
    ).slice(0, 500);
  }, [debouncedInputValue, userOptions]);


  useEffect(() => {
    if (user.value && permissionOption.length > 0) {
      getSingleUserPermission();
    }
  }, [user, permissionOption]);

  const getSingleUserPermission = () => {
    let userId = [user.value];
    // TODO ID HERE in API CALL
    UserPermissionProxy.GetSingleUserPermission(userId, Constants.userToken, (response) =>
      setSelectedPermissions(response)
    );
  };

  const setSelectedPermissions = (response) => {
    let selectedOptions = [];
    response.map((selectedOp) => {
      permissionOption.map((perOp) => {
        if (
          selectedOp.permissionid === perOp.sendValue &&
          selectedOp.type === perOp.type
        ) {
          selectedOptions.push(perOp);
        }
        return selectedOptions;
      });
      return selectedOptions;
    });
    setSelected(selectedOptions);
  };

  const getAllUserPermission = () => {
    PermissionProxy.GetAllPermission((response) => {
      if (response) {
        setAllUserPermissionData(response)
      } else {
        setErrorSnackbar(true)
      }
    }
    );
  };

  const setAllUserPermissionData = (response) => {
    let optionsArray = [];
    let uniqueId = 0;
    response.map((option) => {
      Constants.PERMISSION_TYPE.map((permission) => {
        uniqueId++;
        let option1 = {
          value: uniqueId,
          label: option.name + " - " + permission,
          type: permission,
          sendValue: option.id,
        };
        uniqueId++;
        optionsArray.push(option1);
        return true;
      });
      return true;
    });
    setPermissionOption(optionsArray);
  };

  const handleGrantUserPermission = () => {
    let data = fullOptionToValue(user.value, selected);
    setGrantLoader(true);
    UserPermissionProxy.AddUserPermission(data, (response) => {
      if (response) {
        setSnackMessage(response);
        setSuccessSnackbar(true);
        getSingleUserPermission();
        setGrantLoader(false);
      } else {
        setErrorSnackbar(true);
        setGrantLoader(false);
      }
    });
  };

  const handleRevokeUserPermission = () => {
    let data = fullOptionToValue(user.value, selected);
    setRevokeLoader(true);
    UserPermissionProxy.DeleteUserPermission(data, (response) => {
      if (response) {
        setSnackMessage(response);
        setSuccessSnackbar(true);
        getSingleUserPermission();
        setRevokeLoader(false);
      } else {
        setRevokeLoader(false);
        setErrorSnackbar(true);
      }
    });
  };

  return (
    <Box>
      <TabMenuSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Error"}
        handleClose={() => setErrorSnackbar(false)}
      />
      {successSnackbar ? (
        <TabMenuSnackbar
          show={true}
          message={<Box>{snackMessage.errorMessage}</Box>}
          variant={snackMessage.status.toLowerCase()}
          autoHide={snackMessage.status === "Success" ? true : false}
          handleClose={() => setSuccessSnackbar(false)}
          title={snackMessage.status}
        />
      ) : null}
      {showUserDetailModal ? (
        <UserDetailModal
          show={true}
          handleClose={() => setShowUserDetailModal(false)}
          handleSelect={(selectedOption) => {
            const selectedCustomers = userOptions.filter(opt =>
              selectedOption.includes(opt.value)
            );
            setUser(selectedCustomers[0]);
            setShowUserDetailModal(false)
          }}
          userids={userOptions.filter(option => option.value !== "*").map(cust => cust.value)}
          selectedIds={user !== "" ? [user.value] : []}
          usertype={"U"}
          loading={showUserDetailModal}
          isMulti={false}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          minWidth: "350px",
          maxWidth: "auto",
          gap: "20px",
          mb: 1,
          px: 1
        }}
      >
        <Box sx={{ alignSelf: "center", mt: 2 }}>
          <Typography>Users</Typography>
        </Box>
        <Box
          sx={{
            alignSelf: "center",
            minWidth: "350px",
            maxWidth: "auto",
            mt: 2,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Select menuPlacement="auto"
              blurInputOnSelect={false}
              className="basic-single"
              classNamePrefix="select"
              value={userOptions.find((iuser) => iuser.value === user.value)}
              isSearchable={true}
              name="User"
              menuPortalTarget={document.body}
              styles={Constants.Styles}
              options={filteredUserOptions}
              onChange={(selectedOption) => setUser(selectedOption)}
              inputValue={userInputValue}
              onInputChange={(inputValue) => setUserInputValue(inputValue)}
            />
          </Box>
          <Tooltip title="View More Data">
            <span style={{ cursor: "pointer", marginLeft: "15px", marginTop: "0px" }}>
              <OpenInNewIcon
                fontSize="medium"
                color="action"
                onClick={() => setShowUserDetailModal(true)}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box
        sx={{

          "& .rdl-controls": {
            minHeight: "550px",
            maxHeight: "700px",
          },
          backgroundColor: '#fff',
          color: '#000',
          height: '55vh',
          padding: 1
        }}
      >
        <DualListBox
          canFilter
          filterPlaceholder={"Search Permissions"}
          // showNoOptionsText
          showHeaderLabels
          simpleValue={false}
          options={permissionOption}
          selected={selected}
          onChange={onChange}
          icons={{
            moveLeft: <KeyboardArrowLeftIcon />,
            moveAllLeft: <KeyboardDoubleArrowLeftIcon />,
            moveRight: <KeyboardArrowRightIcon />,
            moveAllRight: <KeyboardDoubleArrowRightIcon />,
            // moveDown: <span className="fa fa-chevron-down" />,
            // moveUp: <span className="fa fa-chevron-up" />,
            // moveTop: <span className="fa fa-double-angle-up" />,
            // moveBottom: <span className="fa fa-double-angle-down" />,
          }}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          paddingBottom: 5,
          paddingRight: 5
        }}
        height={'15vh'}
        display="flex"
        alignItems="end"
        justifyContent="end"
        gap={"15px"}
      >
        {Utility.isUpdateAllowed(local_Permission) ||
          Utility.isAddAllowed(local_Permission) ? (
          <LoadingButton
            loading={grantLoader}
            loadingPosition="start"
            variant="contained"
            startIcon={<AddTaskIcon />}
            onClick={handleGrantUserPermission}
            disabled={userOptions.length === 0 || selected.length === 0 || revokeLoader}
          >
            Grant
          </LoadingButton>
        ) : null}

        {Utility.isUpdateAllowed(local_Permission) ||
          Utility.isDeleteAllowed(local_Permission) ? (
          <LoadingButton
            sx={{ bgcolor: "#1a237e" }}
            variant="contained"
            loading={revokeLoader}
            loadingPosition="start"
            component="label"
            startIcon={<BlockIcon />}
            onClick={handleRevokeUserPermission}
            disabled={userOptions.length === 0 || selected.length === 0 || grantLoader}
          >
            Revoke
          </LoadingButton>
        ) : null}
      </Box>
    </Box>
  );
};

export default SingleUserPermission;
